import React, { useEffect, useRef } from 'react';
import { Camera, Briefcase, Clock } from 'lucide-react';
import { FuturisticBG } from './FuturisticBG';
import { MovingBG } from './MovingBG';
import { gsap, ScrollTrigger } from '../lib/gsap';

export const About = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(sectionRef);

      // ── Heading slides in from LEFT ──
      gsap.from(q('[data-gs="heading"]'), {
        x: -60,
        scrollTrigger: {
          trigger: q('[data-gs="heading"]')[0],
          start: 'top 85%',
          end: 'top 60%',
          scrub: 0.5,
        },
      });

      // ── Image slides in from RIGHT ──
      gsap.from(q('[data-gs="img"]'), {
        x: 80,
        scale: 0.96,
        scrollTrigger: {
          trigger: q('[data-gs="img"]')[0],
          start: 'top 85%',
          end: 'top 50%',
          scrub: 0.5,
        },
      });

      // ── Text block slides in from LEFT ──
      gsap.from(q('[data-gs="text-block"]'), {
        x: -50,
        scrollTrigger: {
          trigger: q('[data-gs="text-block"]')[0],
          start: 'top 85%',
          end: 'top 55%',
          scrub: 0.5,
        },
      });

      // ── Feature cards stagger rise ──
      gsap.from(q('[data-gs="feat-card"]'), {
        y: 40,
        stagger: 0.08,
        scrollTrigger: {
          trigger: q('[data-gs="feat-card"]')[0],
          start: 'top 88%',
          end: 'top 58%',
          scrub: 0.5,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-24 relative overflow-hidden" style={{ background: '#050505' }}>
      <MovingBG />
      <FuturisticBG density={12} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div data-gs="heading" className="text-center mb-16 will-change-transform">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">Who </span>
            <span className="bg-gradient-to-r from-orange-400 to-amber-500 bg-clip-text text-transparent">We Are</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto" />
        </div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: image — clipping wrapper for clean reveal */}
          <div className="overflow-hidden rounded-2xl">
            <div data-gs="img" className="relative group max-w-sm mx-auto md:max-w-none will-change-transform">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition-opacity duration-300" />
              <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '3/4' }}>
                <img
                  src="https://tafworldwide.b-cdn.net/WHO%20WE%20ARE%20MAIN%20PAGE.jpg"
                  alt="TAF Worldwide Team"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  data-testid="who-we-are-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Right: text */}
          <div data-gs="text-block" className="space-y-8 will-change-transform">
            <div className="space-y-4">
              <h3
                className="text-4xl md:text-5xl font-bold text-white leading-tight"
                style={{ fontFamily: "'TafHeadline'", letterSpacing: '0.05em' }}
              >
                TAF WORLDWIDE TEAM
              </h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                We are a creative cinematography team with extensive experience in the industry. Our portfolio spans across diverse projects including{' '}
                <span className="text-orange-400 font-semibold">music artists shoots</span>,{' '}
                <span className="text-amber-400 font-semibold">film production</span>, automotive showcases, and life's most precious moments.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                From working with renowned artists to producing cinematic films, we bring passion, technical expertise, and cutting-edge equipment to every project.
              </p>
            </div>

            {/* Feature cards */}
            <div className="grid grid-cols-1 gap-4">
              {[
                { icon: Camera, colorClasses: 'bg-orange-500/15 text-orange-400', title: 'State of the Art Equipment', desc: 'Professional cinema cameras and the latest gear' },
                { icon: Briefcase, colorClasses: 'bg-amber-500/15 text-amber-400', title: 'Proven Track Record', desc: 'Consistent delivery of exceptional visual content' },
                { icon: Clock, colorClasses: 'bg-orange-500/15 text-orange-400', title: '5 Years Experience', desc: 'Half a decade of capturing stunning visuals' },
              ].map((item) => {
                const Icon = item.icon;
                const [bgClass, textClass] = item.colorClasses.split(' ');
                return (
                  <div
                    key={item.title}
                    data-gs="feat-card"
                    className="flex items-start gap-4 p-5 bg-white/4 rounded-xl border border-white/8 hover:border-orange-500/40 transition-colors duration-300 will-change-transform"
                  >
                    <div className={`p-2.5 ${bgClass} rounded-lg`}>
                      <Icon className={`w-5 h-5 ${textClass}`} />
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
